import { useAtomValue } from 'jotai';
import { lazy, Suspense } from 'react';

import { DisplayTemplatePreviewButton } from '@/components/display-template-preview-button';
import { useDisplayTemplatePreviewPanelBridge } from '@/features/developer-tools/display-template-preview/use-display-template-preview-panel-bridge';
import { useDevToolsHotkeys } from '@/features/developer-tools/hooks/use-dev-tools-hotkeys';
import { devToolsPanelInstancesAtom } from '@/features/developer-tools/state/dev-tools-panel.state';
import { UxdsVersionRollbackButton } from '@/features/developer-tools/uxds-version-rollback/uxds-version-rollback-button';

// Lazy load the panel component
const DevToolsPanel = lazy(() =>
  import('./components/dev-tools-panel').then((module) => ({
    default: module.DevToolsPanel,
  }))
);

/**
 * DeveloperTools - Unified tabbed developer tools panel.
 * Supports multiple instances for side-by-side viewing.
 * Hotkeys:
 * - Cmd/Ctrl+Shift+0: Open to Help tab
 * - Cmd/Ctrl+Shift+1: Open to Device Inspector tab
 * - Cmd/Ctrl+Shift+2: Open to Display Template Preview tab
 * - Cmd/Ctrl+Shift+3: Open to Device UX Editor tab
 * - Cmd/Ctrl+Shift+4: Open to Misconfigured Overlay tab
 * - Cmd/Ctrl+Shift+5: Open to App Inspector tab
 * - Cmd/Ctrl+Shift+6: Open to Websockets tab
 * - Cmd/Ctrl+Shift+7: Open to Third Party Tools tab
 * - Cmd/Ctrl+Shift+8: Open to UXDS Version Rollback tab
 * - Escape: Close topmost panel
 * - Cmd/Ctrl+Shift+Escape: Close all panels
 */
export const DeveloperTools = () => {
  // Handle global hotkeys for dev tools
  useDevToolsHotkeys();
  useDisplayTemplatePreviewPanelBridge();

  const instances = useAtomValue(devToolsPanelInstancesAtom);

  return (
    <Suspense fallback={null}>
      <DisplayTemplatePreviewButton />
      <UxdsVersionRollbackButton />
      {instances.map((instanceId) => (
        <DevToolsPanel key={instanceId} instanceId={instanceId} />
      ))}
    </Suspense>
  );
};
